import {
  Badge,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconCircleFilled, IconClock, IconListNumbers, IconPhoneIncoming, IconUsers } from '@tabler/icons-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { useOrg } from '@/hooks/use-org';
import { isSoftphoneConnected } from '../-components/agent/agent-status';
import { StatTile } from '../-components/stat-cards';
import { toMs } from '../-components/format';
import { useNow } from '../-components/use-now';
import { useSince } from '../-components/use-time-window';
import {
  QueueDashboardAgentStatusSubscription,
  QueueDashboardQueuesSubscription,
  QueueDashboardStatusDefinitionsSubscription,
} from './-components/graphql';

const METRICS_WINDOW_MS = 60 * 60 * 1000;

export const Route = createFileRoute('/_authed/queue-dashboard/')({
  component: QueueDashboardPage,
});

type QueueSummary = {
  id: string;
  name: string;
  enabled: boolean;
  routingStrategy: string;
  depth: number;
  ringing: number;
  activeCalls: number;
  avgWaitSecs: number;
  slaPercent: number;
  availableAgents: number;
  totalAgents: number;
};

function slaColorOf(percent: number) {
  return percent >= 80 ? 'green' : percent >= 60 ? 'yellow' : 'red';
}

function QueueCard({ queue }: { queue: QueueSummary }) {
  return (
    <Link
      to="/queue-dashboard/$queueId"
      params={{ queueId: queue.id }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Paper p="lg" radius="lg" withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Group gap="xs">
              <Text fw={600}>{queue.name}</Text>
              {!queue.enabled && (
                <Badge size="xs" color="gray">
                  Disabled
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed" tt="capitalize">
              {queue.routingStrategy.replace(/_/g, ' ')}
            </Text>
          </div>
          <RingProgress
            size={50}
            thickness={4}
            sections={[{ value: queue.slaPercent, color: slaColorOf(queue.slaPercent) }]}
            label={
              <Text size="xs" ta="center" fw={600}>
                {queue.slaPercent}%
              </Text>
            }
          />
        </Group>

        <SimpleGrid cols={3} spacing="xs">
          <div>
            <Text size="xs" c="dimmed">
              Waiting
            </Text>
            <Text fw={600} size="lg" c={queue.depth > 0 ? 'orange' : undefined}>
              {queue.depth}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              Active
            </Text>
            <Text fw={600} size="lg" c={queue.activeCalls > 0 ? 'green' : undefined}>
              {queue.activeCalls}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              Agents
            </Text>
            <Text fw={600} size="lg">
              {queue.availableAgents}/{queue.totalAgents}
            </Text>
          </div>
        </SimpleGrid>

        {queue.depth > 0 && (
          <Group gap="xs" mt="sm">
            <IconClock size={12} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              Avg wait: {queue.avgWaitSecs}s
            </Text>
          </Group>
        )}
      </Paper>
    </Link>
  );
}

function AgentStatusGrid({ agents }: { agents: Array<{ label: string; color: string }> }) {
  if (agents.length === 0) return null;

  const grouped = new Map<string, { color: string; count: number }>();

  for (const agent of agents) {
    const existing = grouped.get(agent.label);

    grouped.set(agent.label, { color: agent.color, count: (existing?.count ?? 0) + 1 });
  }

  return (
    <Paper p="lg" radius="lg" withBorder>
      <Text fw={600} mb="md">
        Agent Status
      </Text>
      <Group gap="lg">
        {[...grouped.entries()].map(([label, value]) => (
          <Group key={label} gap="xs">
            <IconCircleFilled size={10} color={`var(--mantine-color-${value.color}-6)`} />
            <Text size="sm">
              {label}: <strong>{value.count}</strong>
            </Text>
          </Group>
        ))}
      </Group>
    </Paper>
  );
}

function QueueDashboard({ organizationId }: { organizationId: string }) {
  const since = useSince(METRICS_WINDOW_MS);
  const now = useNow(5000);

  const [{ data: queueData }] = useSubscription({
    query: QueueDashboardQueuesSubscription,
    variables: { organizationId, since },
  });
  const [{ data: statusData }] = useSubscription({
    query: QueueDashboardAgentStatusSubscription,
    variables: { organizationId },
  });
  const [{ data: definitionData }] = useSubscription({
    query: QueueDashboardStatusDefinitionsSubscription,
    variables: { organizationId },
  });

  if (!queueData || !statusData || !definitionData) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const routableNames = new Set(
    definitionData.agentStatusDefinitions.filter((s) => s.availableForRouting).map((s) => s.name),
  );
  const definitionsByName = new Map(definitionData.agentStatusDefinitions.map((s) => [s.name, s]));

  const isRoutable = (status: (typeof statusData.agentStatus)[number]) =>
    routableNames.has(status.statusName) &&
    isSoftphoneConnected(status, now) &&
    !status.currentAgentCallSessionId &&
    !status.currentCallControlId &&
    !status.currentQueueEntryId;

  const statusByMember = new Map(statusData.agentStatus.map((status) => [status.memberId, status]));

  const queues: QueueSummary[] = queueData.queues.map((queue) => {
    const handled = queue.recentEntries.filter((e) => e.status === 'answered' || e.status === 'completed');
    const withinSla = handled.filter(
      (e) => e.answeredAt && toMs(e.answeredAt) - toMs(e.enteredAt) <= (queue.slaTargetSecs ?? 60) * 1000,
    );
    const waits = queue.waitingEntries.map((e) => (now - toMs(e.enteredAt)) / 1000);

    return {
      id: queue.id,
      name: queue.name,
      enabled: queue.enabled,
      routingStrategy: queue.routingStrategy,
      depth: queue.waitingEntries.length,
      ringing: queue.ringing.aggregate?.count ?? 0,
      activeCalls: queue.answered.aggregate?.count ?? 0,
      avgWaitSecs: waits.length > 0 ? Math.round(waits.reduce((sum, w) => sum + w, 0) / waits.length) : 0,
      slaPercent: handled.length > 0 ? Math.round((withinSla.length / handled.length) * 100) : 100,
      availableAgents: queue.queueMembers.filter((qm) => {
        const status = statusByMember.get(qm.memberId);

        return !!status && isRoutable(status);
      }).length,
      totalAgents: queue.queueMembers.length,
    };
  });

  const agents = statusData.agentStatus.map((status) => {
    const definition = definitionsByName.get(status.statusName);
    const isOnline = isSoftphoneConnected(status, now);

    return {
      label: isOnline ? (definition?.label ?? status.statusName) : 'Offline',
      color: isOnline ? (definition?.color ?? 'gray') : 'gray',
    };
  });

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Queue Dashboard</Title>
          <Text c="dimmed" size="sm">
            Real-time contact center monitoring
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <StatTile
            label="Callers Waiting"
            value={queues.reduce((sum, q) => sum + q.depth, 0)}
            icon={<IconPhoneIncoming size={24} />}
            color={queues.some((q) => q.depth > 0) ? 'orange' : 'gray'}
          />
          <StatTile
            label="Active Calls"
            value={queues.reduce((sum, q) => sum + q.activeCalls, 0)}
            icon={<IconListNumbers size={24} />}
            color="green"
          />
          <StatTile
            label="Agents Available"
            value={statusData.agentStatus.filter(isRoutable).length}
            icon={<IconUsers size={24} />}
            color="indigo"
          />
        </SimpleGrid>

        <AgentStatusGrid agents={agents} />

        {queues.length === 0 ? (
          <Paper p="3xl" radius="lg" withBorder>
            <Stack align="center" gap="md">
              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                <IconListNumbers size={24} />
              </ThemeIcon>
              <Text fw={500}>No queues configured</Text>
              <Text size="sm" c="dimmed">
                Create queues in Settings to start routing calls.
              </Text>
            </Stack>
          </Paper>
        ) : (
          <div>
            <Text fw={600} mb="md">
              Queues
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {queues.map((queue) => (
                <QueueCard key={queue.id} queue={queue} />
              ))}
            </SimpleGrid>
          </div>
        )}
      </Stack>
    </Container>
  );
}

function QueueDashboardPage() {
  const { organizationId, isLoading } = useOrg();

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  return <QueueDashboard organizationId={organizationId} />;
}
